import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { AuthServiceHelper } from '@/modules/Auth'
import { UserServiceHelper } from '@/modules/User'
import { useEMTDispatch } from '@/store'
import { Routes } from '@/assets/configs'
import useConnectionRoutes from '@/hooks/useConnectionRoutes'
import { UrlParamType } from '@/modules/utils'
import { appendParamsIfNeeded } from '@/utils'
import { Constants } from '@/assets'
import { useFirstRouterQueryParam } from '@/hooks'
import GlobalLoader from '@/components/GlobalLoader'
import WelcomeLoader from '@/components/WelcomeLoader'
import useCurrentUser from '@/hooks/useCurrentUser'
import { CurrentAccount } from '@/modules/User/apiTypes'

export type withNoAuthRouteProps = unknown

const withNoAuthRoute =
  (redirectPath = Routes.Home) =>
  <Config,>(WrappedComponent: React.FC<Config & withNoAuthRouteProps>) => {
    const NoAuthRoute: React.FC<Config> = (props) => {
      const router = useRouter()
      const dispatch = useEMTDispatch()
      const { currentUser, isOwner } = useCurrentUser()
      const { getNextRoute } = useConnectionRoutes()

      const [isLoading, setLoading] = useState<boolean>(true)
      const isFirstLaunch = useRef(true)
      const redirectLockRef = useRef(false)
      const hasRunRef = useRef(false)

      const [queryType] = useFirstRouterQueryParam(['type'])

      useEffect(() => {
        redirectLockRef.current = false
      }, [router.pathname])

      useEffect(() => {
        hasRunRef.current = false
      }, [router.pathname])

      useEffect(() => {
        const checkIsLoggedAsync = async () => {
          if (hasRunRef.current) return
          hasRunRef.current = true
          try {
            let finalUser: CurrentAccount.Get.Response | null = currentUser || null

            const refreshUser = isFirstLaunch.current
            const { isValid: logged, ...result } = await AuthServiceHelper.dispatchCheckAuthStatusAsync(dispatch)(
              refreshUser,
            )
            isFirstLaunch.current = false
            if (refreshUser) {
              finalUser = result.user
            }

            if (logged) {
              if (!finalUser) {
                finalUser = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
                if (!finalUser) {
                  await AuthServiceHelper.dispatchLogoutAsync(dispatch)()
                }
                return
              }

              const nextRoute = getNextRoute(finalUser, {
                nextPage: redirectPath,
                fromRegister: router.pathname.includes('create-your-profile') || router.pathname.includes('sign-up'),
              })

              const nextUrl = new URL(nextRoute, Constants.apiUrl)

              let loggedType = queryType

              if (
                loggedType !== UrlParamType.Owner &&
                loggedType !== UrlParamType.Borrower &&
                loggedType !== UrlParamType.Pro
              ) {
                loggedType = isOwner ? UrlParamType.Owner : UrlParamType.Borrower
              }

              const finalRoute = appendParamsIfNeeded(nextRoute, {
                type: loggedType,
                nextPage: nextUrl.pathname,
              })
              if (redirectLockRef.current) return
              redirectLockRef.current = true

              if (router.asPath === finalRoute) return

              router.replace(finalRoute)
              return
            }

            setLoading(false)
          } catch (error) {
            isFirstLaunch.current = false
            console.debug(error)
          }
        }

        checkIsLoggedAsync().finally()
      }, [currentUser, isOwner, queryType, router.pathname])

      if (isLoading) {
        return router.pathname !== Routes.Connection.Welcome ? <GlobalLoader /> : <WelcomeLoader />
      }

      // @ts-expect-error: needs to extend IntrinsicAttributes
      return <WrappedComponent {...props} />
    }

    return NoAuthRoute
  }

export default withNoAuthRoute
